
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Dealer } from "@/types/dealer";
import { useDealersInfiniteScroll } from "./useDealersInfiniteScroll";
import { useNavigate } from "react-router-dom";
import { isSSR } from "@/lib/isSSR";
import { useEmbeddedDealerData } from "./useEmbeddedDealerData";
import { DealerFilterState } from "./types";

export function useHydratedDealersPage() {
  console.log("🔄 useHydratedDealersPage: Hook initialized");
  
  const isClient = !isSSR();
  
  // Get embedded dealer data from HTML
  const { dealerData, isLoading: embeddedLoading } = useEmbeddedDealerData();
  
  console.log("📊 useHydratedDealersPage: Data source decision:", {
    isClient,
    isSSR: isSSR(),
    hasEmbeddedData: !!dealerData?.dealers?.length
  });
  
  // Completely stable initial filters
  const initialFilters: Partial<DealerFilterState> = useMemo(() => ({
    sortOrder: "distance" as const,
    filterActivated: false
  }), []);
  
  // Completely stable infinite scroll options
  const infiniteScrollOptions = useMemo(() => ({
    pageSize: 20,
    enableInfiniteScroll: isClient,
    initialFilters
  }), [isClient, initialFilters]);
  
  // Get the infinite scroll dealers data
  const infiniteScrollData = useDealersInfiniteScroll(infiniteScrollOptions);
  
  console.log("📈 useHydratedDealersPage: Infinite scroll data:", {
    dealersCount: infiniteScrollData.dealers.length,
    isLoading: infiniteScrollData.isLoading,
    isLoadingMore: infiniteScrollData.isLoadingMore,
    hasMore: infiniteScrollData.hasMore,
    error: infiniteScrollData.error,
    totalCount: infiniteScrollData.totalCount,
    enabled: isClient
  });
  
  // Final data calculation
  const dealers = isClient ? infiniteScrollData.dealers : (dealerData?.dealers || []);
  const loading = isClient ? infiniteScrollData.isLoading : embeddedLoading;
  
  // Add view state for map/list toggle
  const [view, setView] = useState<"list" | "map">("list");
  const navigate = !isSSR() ? useNavigate() : null;
  
  // Extract unique cities from dealers
  const cities = useMemo(() => {
    if (dealerData && dealerData.cities && dealerData.cities.length > 0) {
      return dealerData.cities;
    }
    return [...new Set(dealers.map(dealer => dealer.city))].filter(Boolean).sort();
  }, [dealerData, dealers]);
  
  console.log("🎲 useHydratedDealersPage: Final state:", {
    source: isClient ? 'infiniteScroll' : 'embedded',
    dealersCount: dealers.length,
    citiesCount: cities.length,
    loading,
    isClient
  });
  
  // Default filters for fallback
  const defaultFilters: DealerFilterState = useMemo(() => ({
    search: "",
    range: [0, 5] as [number, number],
    filterVerified: false,
    filterActivated: false,
    sortOrder: "distance",
    distanceRange: 0,
  }), []);
  
  // Completely stable handler functions
  const handleCardClick = useCallback((dealerId: string, city?: string) => {
    if (isSSR() || !navigate) return;
    
    let citySlug = city ? city.toLowerCase().replace(/\s+/g, '-') : 'roma';
    const currentPath = window.location.pathname;
    const pathParts = currentPath.split('/');
    
    if (pathParts.length >= 3 && pathParts[1] === 'compro-oro' && pathParts[2]) {
      citySlug = pathParts[2];
    }
    
    navigate(`/compro-oro/${citySlug}/${dealerId}`);
  }, [navigate]);
  
  const handleRefresh = useCallback(() => {
    if (isClient && infiniteScrollData.refresh) {
      console.log("🔄 useHydratedDealersPage: Calling refresh");
      return infiniteScrollData.refresh();
    }
    return Promise.resolve();
  }, [isClient, infiniteScrollData.refresh]);
  
  const handleCityClick = useCallback((city: string) => {
    if (isSSR() || !navigate) return;
    const citySlug = city.toLowerCase().replace(/\s+/g, '-');
    navigate(`/compro-oro/${citySlug}`);
  }, [navigate]);
  
  console.log("🏁 useHydratedDealersPage: Final return values:", {
    dealersCount: dealers.length,
    citiesCount: cities.length,
    loading,
    isClient,
    totalCount: isClient ? infiniteScrollData.totalCount : dealers.length,
    hasLoadMore: !!infiniteScrollData.loadMore
  });
  
  // Return completely stable object
  return useMemo(() => ({
    // Data
    dealers,
    cities,
    isLoading: loading,
    
    // Infinite scroll specific
    isLoadingMore: isClient ? infiniteScrollData.isLoadingMore : false,
    hasMore: isClient ? infiniteScrollData.hasMore : false,
    totalCount: isClient ? infiniteScrollData.totalCount : dealers.length,
    enableInfiniteScroll: isClient,
    
    // Filters and view
    filters: isClient ? infiniteScrollData.filters : defaultFilters,
    view,
    setView,
    
    // Actions
    updateFilters: isClient ? infiniteScrollData.updateFilters : () => {},
    handleCardClick,
    handleRefresh,
    handleCityClick,
    loadMore: infiniteScrollData.loadMore, // Direct reference
    
    // Legacy compatibility
    refreshDealers: handleRefresh
  }), [
    dealers,
    cities,
    loading,
    isClient,
    infiniteScrollData.isLoadingMore,
    infiniteScrollData.hasMore,
    infiniteScrollData.totalCount,
    infiniteScrollData.filters,
    infiniteScrollData.updateFilters,
    infiniteScrollData.loadMore,
    view,
    setView,
    handleCardClick,
    handleRefresh,
    handleCityClick,
    defaultFilters
  ]);
}
